/**
 * Keyboard Manager — global keyboard shortcuts
 * Shortcuts match the actual NEVCO Pro Video software:
 *   F1=Help, F3=Properties, F4=Graphics Library, F5=Program Monitor,
 *   F6=Preview, F7=Video Switcher, F8=Audio Mixer, F9=Shot Launcher
 */
import { uiStore } from '../stores/uiStore';
import { selectionStore } from '../stores/selectionStore';
import { projectStore } from '../stores/projectStore';
import { doTake } from '../ui/program';
import { showGettingStarted } from '../ui/menuDialogs';
import { newProjectAction, saveProjectAction } from '../ui/menuSystem';
import type { PanelVisibility } from '@graphicsim/shared-types';

// Panel toggle shortcut map (matches real software View menu)
const panelShortcuts: Record<string, keyof PanelVisibility> = {
  F3: 'properties',
  F4: 'library',
  F5: 'program',
  F6: 'preview',
  F7: 'switcher',
  F8: 'audio',
  F9: 'shots',
};

export function initKeyboardManager(): void {
  document.addEventListener('keydown', handleKeyDown);
}

function handleKeyDown(e: KeyboardEvent): void {
  // Don't intercept when typing in inputs
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  // F1 = Help / Getting Started
  if (e.key === 'F1') {
    e.preventDefault();
    showGettingStarted();
    return;
  }

  // Ctrl+N = New Project
  if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
    e.preventDefault();
    newProjectAction();
    return;
  }

  // Ctrl+S = Save Project
  if ((e.ctrlKey || e.metaKey) && e.key === 's') {
    e.preventDefault();
    saveProjectAction();
    return;
  }

  // Panel toggles (F3-F9)
  if (panelShortcuts[e.key]) {
    e.preventDefault();
    uiStore.getState().togglePanel(panelShortcuts[e.key]);
    return;
  }

  // Take to program (Space or Enter)
  if (e.key === ' ' || e.key === 'Enter') {
    e.preventDefault();
    doTake();
    return;
  }

  // Delete selected items
  if (e.key === 'Delete' || e.key === 'Backspace') {
    const ids = selectionStore.getState().selectedProjectItemIds;
    if (ids.length > 0) {
      e.preventDefault();
      projectStore.getState().removeItems(ids);
      selectionStore.getState().clearSelection();
    }
    return;
  }

  // Escape — clear selection, close menus
  if (e.key === 'Escape') {
    selectionStore.getState().clearSelection();
    document.querySelectorAll('.menu-dropdown, .context-menu, .modal-overlay').forEach((el) => el.remove());
    return;
  }

  // Ctrl+A — select all project items in current channel
  if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
    e.preventDefault();
    const items = projectStore.getState().getChannelItems();
    selectionStore.getState().selectProjectItems(items.map((i) => i.id));
    return;
  }
}
